import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { toPortalFailure, type PortalFailure } from "../lib/errors";
import { refuseInView, sourcedKey, useReadSource, viewRows } from "./readSource";

/**
 * Every read is an ordinary RLS-scoped select; every write is a named RPC.
 *
 * architecture.md section 3B and plan.md section 14.2: direct insert, update and delete are revoked
 * for the browser role, so a write that is not an RPC here would simply fail. Keeping the two kinds
 * of call in one file makes that asymmetry visible instead of incidental.
 */

export const chatMessagesKey = (tenantId: string) => ["portal", "chat", tenantId] as const;
export const notificationsKey = ["portal", "notifications"] as const;
/**
 * A prefix of `notificationsKey` on purpose. React Query matches keys by prefix, so every existing
 * `invalidateQueries({ queryKey: notificationsKey })` refreshes the count as well, and a caller
 * cannot mark something read and leave the bell showing the old number.
 */
export const unreadNotificationCountKey = ["portal", "notifications", "unread-count"] as const;
/**
 * The key for whatever reads `public.v_unread_chat_count`, and the one thing that reader has to know.
 *
 * It was exported with no caller and nothing in `app/` read the view at all, which is measured in
 * `tests/rpc-surface-reachability.test.ts`. `useMarkChatRead` below is the first caller of either
 * half, and it invalidates this key on success so that a count drawn anywhere in the shell cannot
 * keep showing a number the reader has just cleared.
 *
 * **The view inner joins `public.chat_read_positions`.** Its definition is in
 * `20260829120009_indexes_views_rls_realtime_grants.sql`, and a client who has never marked the
 * thread read has no row in that table, so they have no row in the view either. The count is
 * therefore **absent rather than zero** for that person, and a reader that treats an empty answer as
 * "nothing unread" says the opposite of the truth to exactly the client who has never opened the
 * chat and has messages waiting.
 *
 * The view is deliberately not changed here. Marking read on the chat screen makes the row exist
 * from the first visit onward, which turns the absence into a transient state for anybody who has
 * opened the conversation once, and rewriting a `security_invoker` view that Petrovic's surface also
 * reads is wider than the defect this file is fixing. What remains true, and is written down so it
 * is a known state rather than a surprise: for a client who has never opened `/chat`, the view
 * answers with no row, and any summary built on it has to read that absence as "unknown" and not as
 * "noll olästa".
 */
export const unreadChatKey = (tenantId: string) => ["portal", "chat-unread", tenantId] as const;

export interface ChatMessage {
  id: string;
  body: string | null;
  sent_at: string;
  sender_user_id: string | null;
}

export function useChatMessages(tenantId: string | null): UseQueryResult<ChatMessage[]> {
  const source = useReadSource();
  return useQuery({
    queryKey: sourcedKey(source, chatMessagesKey(tenantId ?? "none")),
    enabled: Boolean(tenantId),
    queryFn: async () => {
      if (source.kind === "admin") {
        // The gateway answers the newest 200 newest first, as the client's read does before its reverse.
        const rows = await viewRows<ChatMessage>("admin_as_client_chat", { p_tenant_id: source.tenantId });
        return rows.reverse();
      }
      const supabase = getSupabaseBrowserClient();
      /*
       * The newest 200, put back in reading order. Until 2026-09-25 this asked for the first 200 in
       * reading order, which is the oldest 200: a conversation that passed 200 messages would have
       * stopped growing on the client's screen and their newest message would never have appeared,
       * nor been marked read. Found while answering why "Bifogar underlaget." showed no file on
       * staging (build round 2, task B); Nordvik Bygg has 160 messages there, so no screen had met it.
       */
      const { data, error } = await supabase
        .from("chat_messages")
        .select("id, body, sent_at, sender_user_id")
        .order("sent_at", { ascending: false })
        .order("id", { ascending: false })
        .limit(200);

      if (error) {
        throw error;
      }
      return ((data ?? []) as ChatMessage[]).reverse();
    }
  });
}

export interface PortalNotification {
  id: string;
  title: string;
  body: string;
  category: string;
  created_at: string;
  read_at: string | null;
  /**
   * Present only when the notification is about a request, which a check constraint enforces.
   *
   * It is a column rather than something interpolated into `title` or `body`, because those two are
   * fixed sanitized summaries and must stay free of row data. Rendering it separately is what lets
   * the reader see which request moved without the summary ever carrying anything a person wrote.
   */
  request_number: number | null;
  /**
   * What the notice is about, when it is about a row rather than about the account.
   *
   * Both columns have been granted to `authenticated` since migration 7 and neither was selected
   * until 2026-09-12, which cost the one-time secret feature its route the day it was built: every
   * `security` notice fell through `destinationFor` to `/settings`, and "En hemlighet väntar på er"
   * led to the password panel. `request_number` above is the same idea for a request and is kept
   * separate because it is a number a client reads out loud, while this is a UUID nobody says.
   *
   * `subject_id` is a link target and never authority. `/secrets/:id` re-decides who may open the
   * transfer from the row itself, so a forged id in this column would buy a reader nothing.
   */
  subject_type: string | null;
  subject_id: string | null;
}

export function useNotifications(): UseQueryResult<PortalNotification[]> {
  return useQuery({
    queryKey: notificationsKey,
    queryFn: async () => {
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase
        .from("notifications")
        .select(
          "id, title, body, category, created_at, read_at, request_number, subject_type, subject_id"
        )
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) {
        throw error;
      }
      return (data ?? []) as PortalNotification[];
    }
  });
}

/**
 * How many notifications are unread, asked as a question rather than counted from a page of rows.
 *
 * The shell used to count `read_at === null` over `useNotifications`, which reads the fifty newest.
 * That is wrong in a way that gets worse with age: once a person has more than fifty notifications,
 * an unread one that has fallen off the end of that page stops being counted, and the bell says
 * "inga olästa" while something is unread. The retention class on this table is
 * `portal_active_plus_12m`, so fifty is a number a real client passes.
 *
 * `head: true` sends no rows back at all. The shell no longer fetches fifty notification bodies on
 * every page in order to draw one dot.
 */
export function useUnreadNotificationCount(): UseQueryResult<number> {
  return useQuery({
    queryKey: unreadNotificationCountKey,
    queryFn: async () => {
      const supabase = getSupabaseBrowserClient();
      const { count, error } = await supabase
        .from("notifications")
        .select("id", { count: "exact", head: true })
        .is("read_at", null);

      if (error) {
        throw error;
      }
      return count ?? 0;
    }
  });
}

export interface SendChatMessageInput {
  tenantId: string;
  body: string;
  /** Retries reuse the key so a resend cannot double-post. */
  idempotencyKey: string;
}

/**
 * The single write path this slice exercises.
 *
 * The tenant is passed because the gateway's signature takes it, and the gateway then checks it
 * against the caller's own membership rather than trusting it. Sending tenant B's id from here
 * produces `PORTAL_FORBIDDEN_TENANT_WRITE`, which is one of the attack tests.
 */
export function useSendChatMessage() {
  const source = useReadSource();
  const queryClient = useQueryClient();

  return useMutation<{ id: string }, PortalFailure, SendChatMessageInput>({
    mutationFn: async ({ tenantId, body, idempotencyKey }) => {
      refuseInView(source);
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase.rpc("send_chat_message", {
        p_tenant_id: tenantId,
        p_body: body,
        p_idempotency_key: idempotencyKey
      });

      if (error) {
        throw toPortalFailure(error);
      }
      return data as unknown as { id: string };
    },
    onSuccess: (_result, variables) =>
      queryClient.invalidateQueries({ queryKey: chatMessagesKey(variables.tenantId) })
  });
}

export interface MarkChatReadInput {
  tenantId: string;
  /**
   * The newest message the reader has in front of them, or null for "everything up to now".
   *
   * The gateway stores it and coalesces a null over an existing value, so passing the id is strictly
   * more informative: `last_read_at` alone cannot say which message the position was taken at, and
   * the id is what a later screen would need to place the divider.
   */
  lastReadMessageId: string | null;
}

/**
 * Marks the tenant's one conversation read for this person. plan.md section 5.2, "Unread state
 * works per user."
 *
 * **This gateway has existed since the first chat migration and had no caller anywhere in the
 * product.** `public.mark_chat_read` is in `20260829120005_chat_requests_attachments.sql` and has
 * been granted to `authenticated` since `20260829120009`. Measured on 2026-09-12 by
 * `tests/rpc-surface-reachability.test.ts`, which used it as its control precisely because nothing
 * called it. Petrovic's own side got `admin_mark_client_chat_read` in migration 61; the client's
 * side never got one, so `public.v_unread_chat_count` could only ever go up for a client.
 *
 * **It is not gated on write access, and that is the gateway's own decision rather than a leniency
 * here.** Every other client write gateway opens with
 * `p_tenant_id = any (public.current_write_tenant_ids())`. This one opens with
 * `public.current_tenant_ids()` and says why in a comment on its own face: marking read stays
 * available in the read-only window. A client whose management has ended can still read the
 * conversation, and a portal that let the unread count climb while refusing the act that clears it
 * would be showing them a number they are not allowed to answer. So the caller must not add a
 * `canWrite` check that the database deliberately does not make.
 *
 * **The rate limit is 60 per hour on `read_position`, per person**, and it is shared with nothing
 * else. `app/routes/ChatRoute.tsx` spends it once per distinct newest message rather than once per
 * render or once per refetch, and the effect that does it says how that is held to.
 *
 * Two refusals are ordinary rather than exceptional. `PORTAL_NO_CHAT_THREAD` comes back when the
 * tenant has no `public.chat_threads` row, which is every tenant that has never sent or received a
 * message, and `PORTAL_CROSS_TENANT_MESSAGE` comes back for a message id that is not in the tenant
 * named. Both are already in `app/lib/errors.ts`.
 */
export function useMarkChatRead() {
  const source = useReadSource();
  const queryClient = useQueryClient();

  return useMutation<void, PortalFailure, MarkChatReadInput>({
    mutationFn: async ({ tenantId, lastReadMessageId }) => {
      // The chat screen fires this on mount, so in the view it is a silent no-op rather than a thrown
      // refusal, and no call leaves: Petrovic looking must never move a client's read position.
      if (source.kind === "admin") {
        return;
      }
      const supabase = getSupabaseBrowserClient();
      const { error } = await supabase.rpc("mark_chat_read", {
        p_tenant_id: tenantId,
        // Omitted rather than sent as null, because the generated argument type is optional and not
        // nullable. The gateway's own default is null and it coalesces a null over what is stored.
        ...(lastReadMessageId ? { p_last_read_message_id: lastReadMessageId } : {})
      });

      if (error) {
        throw toPortalFailure(error);
      }
    },
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: unreadChatKey(variables.tenantId) });
    }
  });
}

/**
 * Empties the inbox in one call.
 *
 * Fifty `mark_notification_read` calls would be fifty transactions, and a run that stopped halfway
 * would leave the bell quiet with unread rows behind it. The gateway does the whole set or none of
 * it, and returns how many it changed, which is the number this screen has no other way to know:
 * the list holds fifty rows and the unread total is counted separately.
 */
export function useMarkAllNotificationsRead() {
  const source = useReadSource();
  const queryClient = useQueryClient();

  return useMutation<number, PortalFailure, void>({
    mutationFn: async () => {
      refuseInView(source);
      const supabase = getSupabaseBrowserClient();
      const { data, error } = await supabase.rpc("mark_all_notifications_read");

      if (error) {
        throw toPortalFailure(error);
      }
      return (data as unknown as number) ?? 0;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: notificationsKey })
  });
}

export function useMarkNotificationRead() {
  const source = useReadSource();
  const queryClient = useQueryClient();

  return useMutation<void, PortalFailure, { notificationId: string }>({
    mutationFn: async ({ notificationId }) => {
      refuseInView(source);
      const supabase = getSupabaseBrowserClient();
      const { error } = await supabase.rpc("mark_notification_read", {
        p_notification_id: notificationId
      });

      if (error) {
        throw toPortalFailure(error);
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: notificationsKey });
    }
  });
}
